import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, LockKeyhole, PencilLine, X } from 'lucide-react';
import { api } from '../../api';
import type { PortalRole } from '../../app/navigation';

type ClosedTicketControlsProps = {
  ticketId: string;
  status: string;
  closedAt?: string | null;
  role: PortalRole;
  onUpdated?: () => void;
};

const PRIVILEGED_ROLES = new Set<PortalRole>(['ADMIN', 'MANAGER']);

export function canChangeClosedTicketStatus(status: string, role: PortalRole) {
  return status !== 'CLOSED' || PRIVILEGED_ROLES.has(role);
}

function toLocalInput(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function displayDate(value?: string | null) {
  if (!value) return 'Дата не указана';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Дата не указана';
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long', timeStyle: 'short' }).format(date);
}

function errorMessage(error: unknown) {
  return error instanceof Error && error.message ? error.message : 'Не удалось изменить дату закрытия';
}

export function ClosedTicketControls({ ticketId, status, closedAt, role, onUpdated }: ClosedTicketControlsProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toLocalInput(closedAt));
  const [error, setError] = useState('');
  const canEdit = PRIVILEGED_ROLES.has(role);

  useEffect(() => {
    if (!editing) setDraft(toLocalInput(closedAt));
  }, [closedAt, editing]);

  const mutation = useMutation({
    mutationFn: (nextClosedAt: string) => api(`/tickets/${ticketId}/closed-at`, {
      method: 'PATCH',
      body: JSON.stringify({ closedAt: nextClosedAt }),
    }),
    onSuccess: async () => {
      setEditing(false);
      setError('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] }),
        queryClient.invalidateQueries({ queryKey: ['timeline', ticketId] }),
        queryClient.invalidateQueries({ queryKey: ['billing'] }),
        queryClient.invalidateQueries({ queryKey: ['billing-summary'] }),
      ]);
      onUpdated?.();
    },
    onError: reason => setError(errorMessage(reason)),
  });

  if (status !== 'CLOSED') return null;

  const save = () => {
    setError('');
    const date = new Date(draft);
    if (!draft || Number.isNaN(date.getTime())) {
      setError('Укажите корректную дату закрытия');
      return;
    }
    if (date.getTime() > Date.now()) {
      setError('Дата закрытия не может быть в будущем');
      return;
    }
    mutation.mutate(date.toISOString());
  };

  return (
    <section className="surface rail-card closed-ticket-control">
      <div className="closed-ticket-control-heading">
        <span className="closed-ticket-control-icon"><LockKeyhole /></span>
        <div><h2>Заявка закрыта</h2><p>Расчёт относится к месяцу закрытия</p></div>
        {canEdit && !editing && (
          <button type="button" className="closed-ticket-edit" aria-label="Изменить дату закрытия" onClick={() => setEditing(true)}>
            <PencilLine />
          </button>
        )}
      </div>

      {editing ? (
        <div className="closed-ticket-editor">
          <label>
            <span><CalendarClock /> Дата и время закрытия</span>
            <input
              type="datetime-local"
              value={draft}
              max={toLocalInput(new Date().toISOString())}
              onChange={event => setDraft(event.target.value)}
              aria-label="Дата и время закрытия"
            />
          </label>
          {error && <p role="alert">{error}</p>}
          <div>
            <button type="button" className="closed-ticket-cancel" onClick={() => { setEditing(false); setError(''); }}><X /> Отмена</button>
            <button type="button" className="closed-ticket-save" aria-label="Сохранить дату закрытия" disabled={mutation.isPending} onClick={save}><Check /> {mutation.isPending ? 'Сохранение…' : 'Сохранить'}</button>
          </div>
        </div>
      ) : (
        <div className="closed-ticket-date">
          <span><CalendarClock /></span>
          <div><small>Дата закрытия</small><b>{displayDate(closedAt)}</b></div>
        </div>
      )}

      {!canEdit && <p className="closed-ticket-lock-note">Изменения защищены. Стадию и трудозатраты может изменить только руководитель или администратор.</p>}
    </section>
  );
}
